import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownField,
} from '@roger/ui-components';
import {
  checkPlannedVisits,
  createMiscTask,
  createOrder,
  createPatientCommunication,
  createPatientVisit,
} from '../../services/api';
import type {
  AvailableClinicianResponse as AvailableClinician,
  BlockedPlannedVisit,
  ClinicPhysicianResponse,
  EpisodeSummaryResponse,
  MiscTaskTypeId,
  OrderTypeId,
  PatientCommunicationResponse,
  PatientCommunicationTypeId,
  VisitTypeId,
} from '../../services/api/types.gen';
import type { TaskTypeOption } from '../../hooks/useVisitTypeOptions';
import { useSchedulingPointLoads } from '../../hooks/useSchedulingPointLoads';
import { ClinicianLoadBadge } from '../scheduling/ClinicianLoadBadge';
import { ClinicianLoadDetail } from '../scheduling/ClinicianLoadDetail';
import { ClinicianSelect } from '../clinician/ClinicianSelect';
import { TaskTypeCombobox } from '../task/TaskTypeCombobox';
import {
  createCreatableTaskTypeOptions,
  createDefaultTaskTypeFilter,
  DEFAULT_NEW_TASK_TYPE,
  filterCreatableTaskTypeOptions,
  getDefaultCreatableTaskTypeOptionId,
  type CreatableTaskTypeOption,
  type TaskTypeFilterValue,
} from '../task/taskTypeFilters';
import {
  TASK_TYPE_DISCIPLINE_MAP,
  isOrderType,
  type TaskTypeId,
} from '../../constants/visitTypes';
import {
  getAssigneeDisciplineForTaskType,
  type ClinicianSelectInput,
} from '../../utils/clinicianUtils';
import {
  formatDateToISO,
  getTodayInPT,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import { formatPhysicianName } from '../../utils/formatting';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { socConflictCopyFromError } from '../visit/socConflictCopy';
import { THIRTY_DAY_SUMMARY_COMMUNICATION_TYPE } from './patientCommunicationTypes';
import {
  clampDateYmdToEpisode,
  dateFromYmd,
  episodeBlocksNewVisits,
  type AddTaskTargetEpisode,
} from './patientDetailTaskConstants';

export interface AddTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Date the dialog opens on (a calendar day click); today when absent. */
  initialDate?: Date;
  /** The table's task-type tab, so Add Task opens on the discipline in view. */
  initialFilter?: TaskTypeFilterValue;
  patientId: string;
  addTaskTargetEpisode: AddTaskTargetEpisode | null;
  /** Only to say whether there is an active episode to be sent to. */
  episodes: EpisodeSummaryResponse[];
  visitTypeOptions: TaskTypeOption[];
  getClinicAssignableClinicians: (
    clinicId: string | null | undefined,
    discipline?: (typeof TASK_TYPE_DISCIPLINE_MAP)[TaskTypeId],
    alwaysIncludeClinicianId?: string | null
  ) => AvailableClinician[];
  getClinicPhysicians: (
    clinicId: string | null | undefined
  ) => ClinicPhysicianResponse[];
  formatDuplicateClinicianOptionLabel: (
    clinician: ClinicianSelectInput,
    label: string
  ) => string;
  episodeRequiresAuthorization: boolean;
  effectiveSelectedEpisodeId: string | null;
  tasksRefreshVersion: number;
  canViewSchedulingPointLoads: boolean;
  patientClinicId: string | null;
  /** Reload the task list once a task has been created. */
  onCreated: () => Promise<unknown> | unknown;
  /** A created communication joins the page's list before the refresh lands. */
  onCommunicationCreated: (communication: PatientCommunicationResponse) => void;
  invalidateSchedulingPointLoads: () => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
}

/**
 * Add Task for the patient chart.
 *
 * The form body is a separate component mounted only while the dialog is open,
 * which is what resets the draft between openings: the page used to hold this
 * state and had to reset it by hand on every open, close and patient switch.
 */
export const AddTaskDialog: React.FC<AddTaskDialogProps> = props => {
  const { open, onOpenChange } = props;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && <AddTaskDialogForm {...props} />}
    </Dialog>
  );
};

const AddTaskDialogForm: React.FC<AddTaskDialogProps> = ({
  onOpenChange,
  initialDate,
  initialFilter,
  patientId,
  addTaskTargetEpisode,
  episodes,
  visitTypeOptions,
  getClinicAssignableClinicians,
  getClinicPhysicians,
  formatDuplicateClinicianOptionLabel,
  episodeRequiresAuthorization,
  effectiveSelectedEpisodeId,
  tasksRefreshVersion,
  canViewSchedulingPointLoads,
  patientClinicId,
  onCreated,
  onCommunicationCreated,
  invalidateSchedulingPointLoads,
  showSuccess,
  showError,
}) => {
  const addTaskOptions = useMemo(
    () => createCreatableTaskTypeOptions(visitTypeOptions),
    [visitTypeOptions]
  );
  const addTaskTargetClinicId =
    addTaskTargetEpisode?.agency_clinic_id ?? undefined;

  // The bounds the form opens against. Read once per mount, like every other
  // initial value here: the clamp effect below follows the episode afterwards.
  const initialTargetEpisodeRef = useRef(addTaskTargetEpisode);
  const [addTaskTypeFilter, setAddTaskTypeFilter] =
    useState<TaskTypeFilterValue>(
      () => initialFilter ?? createDefaultTaskTypeFilter()
    );
  const [selectedAddTaskOptionId, setSelectedAddTaskOptionId] = useState<
    CreatableTaskTypeOption['id']
  >(() =>
    getDefaultCreatableTaskTypeOptionId(
      addTaskOptions,
      initialFilter ?? createDefaultTaskTypeFilter(),
      DEFAULT_NEW_TASK_TYPE
    )
  );
  // A tab that matches nothing yet is remembered rather than dropped:
  // `visitTypeOptions` can still be in flight when the dialog opens, and the
  // effect below applies the tab once its options exist.
  const [pendingAddTaskFilter] = useState<TaskTypeFilterValue | null>(() => {
    const filter = initialFilter ?? createDefaultTaskTypeFilter();
    return filter !== createDefaultTaskTypeFilter() &&
      filterCreatableTaskTypeOptions(addTaskOptions, filter).length === 0
      ? filter
      : null;
  });
  const pendingAddTaskFilterRef = useRef<TaskTypeFilterValue | null>(
    pendingAddTaskFilter
  );
  const [newTaskAssignee, setNewTaskAssignee] = useState<string>('');
  const [newTaskDate, setNewTaskDate] = useState<Date | undefined>(() =>
    dateFromYmd(
      clampDateYmdToEpisode(
        initialDate ? formatDateToISO(initialDate) : getTodayInPT(),
        initialTargetEpisodeRef.current
      )
    )
  );
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [newCommNotes, setNewCommNotes] = useState('');
  const [newTaskPhysicianId, setNewTaskPhysicianId] = useState('');

  const selectedAddTaskOption = useMemo(
    () =>
      addTaskOptions.find(option => option.id === selectedAddTaskOptionId) ??
      null,
    [addTaskOptions, selectedAddTaskOptionId]
  );
  const selectedAddTaskIsCommunication =
    selectedAddTaskOption?.taskKind === 'communication';
  const selectedAddTaskIsMisc = selectedAddTaskOption?.taskKind === 'misc';
  const selectedAddTaskType = selectedAddTaskOption?.id as
    | VisitTypeId
    | PatientCommunicationTypeId
    | MiscTaskTypeId
    | undefined;
  // The 30-Day Summary is filled out on its dedicated detail page, so the Add
  // Task modal neither shows nor requires communication notes for it.
  const selectedAddTaskIsThirtyDaySummary =
    selectedAddTaskIsCommunication &&
    selectedAddTaskType === THIRTY_DAY_SUMMARY_COMMUNICATION_TYPE;
  const selectedAddTaskIsOrder =
    !!selectedAddTaskOption &&
    !selectedAddTaskIsCommunication &&
    !selectedAddTaskIsMisc &&
    isOrderType(selectedAddTaskOption.id as TaskTypeId);
  // Everything that is not an order, communication or misc task is created
  // through the visit endpoint, which requires an active episode.
  const selectedAddTaskIsVisit =
    !!selectedAddTaskOption &&
    !selectedAddTaskIsCommunication &&
    !selectedAddTaskIsMisc &&
    !selectedAddTaskIsOrder;

  // EMR-706: surface the authorization block before submit (not just as a
  // toast on failure), by asking the server whether this exact visit would be
  // refused. The rule lives in `app/services/authorization_errors.py` only --
  // this page renders its answer rather than re-deriving which authorization
  // covers the date and what counts as used.
  const [addTaskCapacityBlock, setAddTaskCapacityBlock] =
    useState<BlockedPlannedVisit | null>(null);
  useEffect(() => {
    const episodeId = effectiveSelectedEpisodeId;
    if (
      !selectedAddTaskIsVisit ||
      !selectedAddTaskType ||
      !newTaskDate ||
      !episodeId ||
      // Most payers don't require an authorization, so the check can only ever
      // answer "not blocked" for them -- don't spend a round trip per date or
      // task-type change to hear that. Matches ScheduleVisitsDialog.
      !episodeRequiresAuthorization
    ) {
      setAddTaskCapacityBlock(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await checkPlannedVisits({
          path: { episode_id: episodeId },
          body: {
            visits: [
              {
                visit_type: selectedAddTaskType as VisitTypeId,
                visit_date: formatDateToISO(newTaskDate),
              },
            ],
          },
        });
        if (cancelled) return;
        if (res.error) throw res.error;
        setAddTaskCapacityBlock(res.data?.blocked?.[0] ?? null);
      } catch (error) {
        if (cancelled) return;
        // Fail open: the create path still enforces the block, so a failed
        // check must not disable Add Task and claim an authorization is
        // missing when one exists.
        console.error('Failed to check authorization capacity', error);
        setAddTaskCapacityBlock(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    selectedAddTaskIsVisit,
    selectedAddTaskType,
    newTaskDate,
    effectiveSelectedEpisodeId,
    episodeRequiresAuthorization,
    tasksRefreshVersion,
  ]);

  const targetEpisodeBlocksVisits =
    episodeBlocksNewVisits(addTaskTargetEpisode);
  const addTaskBlockedByInactiveEpisode =
    selectedAddTaskIsVisit && targetEpisodeBlocksVisits;
  // A discharged patient has no active episode to switch to, so the "go to the
  // active episode" instruction would send them looking for something that does
  // not exist.
  const patientHasActiveEpisode = episodes.some(e => e.status === 'active');

  const addTaskAssigneeDiscipline = useMemo(
    () =>
      getAssigneeDisciplineForTaskType(selectedAddTaskType, {
        isOrder: selectedAddTaskIsOrder,
        isCommunication: selectedAddTaskIsCommunication,
        isMisc: selectedAddTaskIsMisc,
      }),
    [
      selectedAddTaskIsCommunication,
      selectedAddTaskIsMisc,
      selectedAddTaskIsOrder,
      selectedAddTaskType,
    ]
  );
  const addTaskAssignableClinicians = useMemo(
    () =>
      getClinicAssignableClinicians(
        addTaskTargetClinicId,
        addTaskAssigneeDiscipline
      ),
    [
      addTaskAssigneeDiscipline,
      addTaskTargetClinicId,
      getClinicAssignableClinicians,
    ]
  );
  // The week the Add Task dialog is currently pointed at. `newTaskDate` is
  // live in-dialog state, so changing the date re-derives this and the
  // annotation follows — a scheduler moving a visit from Tuesday to the
  // following week must not be reading last week's numbers.
  const addTaskDateYmd = newTaskDate ? formatDateToISO(newTaskDate) : null;
  const { week: addTaskLoadWeek, loadsByUserId: addTaskLoadsByUserId } =
    useSchedulingPointLoads({
      clinicId: addTaskTargetClinicId,
      date: addTaskDateYmd,
      // No clinic, no permission, or a task type with no assignee field at all:
      // no request. The 30-Day Summary is assigned on its own detail page, so
      // the dialog hides the picker the loads annotate.
      enabled:
        canViewSchedulingPointLoads && !selectedAddTaskIsThirtyDaySummary,
    });
  const selectedAddTaskAssigneeUserId =
    addTaskAssignableClinicians.find(c => c.id === newTaskAssignee)?.user_id ??
    null;
  const selectedAddTaskLoad = selectedAddTaskAssigneeUserId
    ? addTaskLoadsByUserId.get(selectedAddTaskAssigneeUserId)
    : undefined;

  const addTaskPhysicianOptions = useMemo(
    () => getClinicPhysicians(addTaskTargetClinicId),
    [addTaskTargetClinicId, getClinicPhysicians]
  );

  useEffect(() => {
    if (
      newTaskAssignee &&
      !addTaskAssignableClinicians.some(c => c.id === newTaskAssignee)
    ) {
      setNewTaskAssignee('');
    }
  }, [addTaskAssignableClinicians, newTaskAssignee]);

  useEffect(() => {
    if (!(selectedAddTaskIsOrder || selectedAddTaskIsCommunication)) {
      setNewTaskPhysicianId('');
      return;
    }

    const defaultPhysicianId = addTaskTargetEpisode?.episode_physician_id;
    const defaultPhysicianIsAvailable = addTaskPhysicianOptions.some(
      physician => physician.id === defaultPhysicianId
    );
    setNewTaskPhysicianId(
      defaultPhysicianId && defaultPhysicianIsAvailable
        ? defaultPhysicianId
        : ''
    );
  }, [
    addTaskTargetEpisode?.episode_physician_id,
    addTaskTargetEpisode?.id,
    addTaskPhysicianOptions,
    selectedAddTaskIsCommunication,
    selectedAddTaskIsOrder,
  ]);

  const setBoundedNewTaskDate = useCallback(
    (date: Date | undefined) => {
      if (!date) {
        setNewTaskDate(undefined);
        return;
      }
      setNewTaskDate(
        dateFromYmd(
          clampDateYmdToEpisode(formatDateToISO(date), addTaskTargetEpisode)
        )
      );
    },
    [addTaskTargetEpisode]
  );

  useEffect(() => {
    const filter = pendingAddTaskFilterRef.current;
    if (!filter || visitTypeOptions.length === 0) {
      return;
    }

    if (filterCreatableTaskTypeOptions(addTaskOptions, filter).length === 0) {
      return;
    }

    setAddTaskTypeFilter(filter);
    setSelectedAddTaskOptionId(
      getDefaultCreatableTaskTypeOptionId(
        addTaskOptions,
        filter,
        DEFAULT_NEW_TASK_TYPE
      )
    );
    pendingAddTaskFilterRef.current = null;
  }, [addTaskOptions, visitTypeOptions.length]);

  useEffect(() => {
    if (!newTaskDate || !addTaskTargetEpisode) {
      return;
    }
    const currentYmd = formatDateToISO(newTaskDate);
    const clampedYmd = clampDateYmdToEpisode(currentYmd, addTaskTargetEpisode);
    if (clampedYmd !== currentYmd) {
      setNewTaskDate(dateFromYmd(clampedYmd));
    }
  }, [addTaskTargetEpisode, newTaskDate]);

  const closeAddTaskDialog = useCallback(
    () => onOpenChange(false),
    [onOpenChange]
  );

  const handleCreateTask = async () => {
    if (!patientId) return;
    if (!newTaskDate) {
      showError('Validation Error', 'Please select a target date.');
      return;
    }
    const taskDate = formatDateToISO(newTaskDate);
    const boundedTaskDate = clampDateYmdToEpisode(
      taskDate,
      addTaskTargetEpisode
    );
    if (boundedTaskDate !== taskDate) {
      showError(
        'Validation Error',
        'Please select a target date within the episode.'
      );
      setNewTaskDate(dateFromYmd(boundedTaskDate));
      return;
    }

    if (!selectedAddTaskOption || !selectedAddTaskType) {
      showError('Validation Error', 'Please select a task type.');
      return;
    }

    if (selectedAddTaskIsMisc) {
      // Misc tasks need a date and an assignee to be signable; no physician and
      // no notes requirement (the attached document is the record).
      if (!newTaskAssignee) {
        showError('Validation Error', 'Please select a clinician.');
        return;
      }
      setIsCreatingTask(true);
      try {
        const response = await createMiscTask({
          path: { patient_id: patientId },
          body: {
            ...(addTaskTargetEpisode
              ? { episode_id: addTaskTargetEpisode.id }
              : {}),
            misc_task_type: selectedAddTaskType as MiscTaskTypeId,
            task_date: taskDate,
            assigned_clinician_id: newTaskAssignee,
          },
        });
        if (response.error) {
          console.error('Failed to create misc task:', response.error);
          const serverMessage = getBusinessErrorMessage(response);
          showError('Create Failed', serverMessage ?? 'Failed to create task.');
          return;
        }
        if (response.data) {
          await onCreated();
          closeAddTaskDialog();
          showSuccess('Task Created', 'New task added successfully.');
        }
      } catch (err) {
        console.error('Error creating misc task:', err);
        showError('Create Failed', 'Failed to create task.');
      } finally {
        setIsCreatingTask(false);
      }
      return;
    }

    if (selectedAddTaskIsCommunication) {
      if (!newTaskPhysicianId) {
        showError('Validation Error', 'Please select a physician.');
        return;
      }
      // Communications now follow the standard two-signer flow, so they must
      // have an assigned clinician to be signable (ROG-2553). 30-Day Summaries
      // keep their own assignment path and are exempt here.
      if (!selectedAddTaskIsThirtyDaySummary && !newTaskAssignee) {
        showError('Validation Error', 'Please select a clinician.');
        return;
      }
      if (!selectedAddTaskIsThirtyDaySummary && !newCommNotes.trim()) {
        showError('Validation Error', 'Please enter communication details.');
        return;
      }
      setIsCreatingTask(true);
      try {
        const communicationBody = {
          ...(addTaskTargetEpisode
            ? { episode_id: addTaskTargetEpisode.id }
            : {}),
          communication_date: taskDate,
          communication_type: selectedAddTaskType as PatientCommunicationTypeId,
          assigned_clinician_id: selectedAddTaskIsThirtyDaySummary
            ? null
            : newTaskAssignee || null,
          clinic_physician_id: newTaskPhysicianId,
          notes: newCommNotes.trim(),
        };
        const response = await createPatientCommunication({
          path: { patient_id: patientId },
          body: communicationBody,
        });
        if (response.error) {
          console.error('Failed to create communication:', response.error);
          showError('Create Failed', 'Failed to create communication.');
          return;
        }
        if (response.data) {
          onCommunicationCreated(response.data);
          await onCreated();
          invalidateSchedulingPointLoads();
          closeAddTaskDialog();
          showSuccess(
            'Communication Created',
            'New communication added successfully.'
          );
        }
      } catch (err) {
        console.error('Error creating communication:', err);
        showError('Create Failed', 'Failed to create communication.');
      } finally {
        setIsCreatingTask(false);
      }
      return;
    }

    if (!newTaskAssignee) {
      showError('Validation Error', 'Please select an assignee.');
      return;
    }
    if (selectedAddTaskIsOrder && !newTaskPhysicianId) {
      showError('Validation Error', 'Please select a physician.');
      return;
    }

    setIsCreatingTask(true);
    try {
      const visitOrOrderType = selectedAddTaskType as TaskTypeId;
      const discipline = TASK_TYPE_DISCIPLINE_MAP[visitOrOrderType];

      const response = selectedAddTaskIsOrder
        ? await createOrder({
            body: {
              ...(addTaskTargetEpisode
                ? { episode_id: addTaskTargetEpisode.id }
                : { patient_id: patientId }),
              order_type: visitOrOrderType as OrderTypeId,
              order_date: taskDate,
              assigned_clinician_id: newTaskAssignee,
              clinic_physician_id: newTaskPhysicianId,
              discipline,
            },
          })
        : await createPatientVisit({
            path: { patient_id: patientId },
            body: {
              ...(addTaskTargetEpisode
                ? { episode_id: addTaskTargetEpisode.id }
                : {}),
              visit_type: visitOrOrderType as VisitTypeId,
              visit_date: taskDate,
              assigned_clinician_id: newTaskAssignee,
              discipline,
            },
          });

      if (response.error) {
        console.error('Failed to create task:', response.error);
        // Surface a server-provided business message when present (e.g.
        // "Only one CMS-485 can be created per episode."), but never a 5xx body
        // or raw Pydantic 422 field errors: getBusinessErrorMessage returns
        // only a 4xx string / `{ message }` detail, so 5xx strings and 422
        // arrays fall through to the generic message and we don't leak backend
        // `body.<field>` paths.
        const serverMessage =
          socConflictCopyFromError(response) ??
          getBusinessErrorMessage(response);
        showError('Create Failed', serverMessage ?? 'Failed to create task.');
        return;
      }
      if (response.data) {
        await onCreated();
        invalidateSchedulingPointLoads();
        closeAddTaskDialog();
        showSuccess('Task Created', 'New task added successfully.');
      }
    } catch (err) {
      console.error('Error creating task:', err);
      showError('Create Failed', 'Failed to create task.');
    } finally {
      setIsCreatingTask(false);
    }
  };

  return (
    <DialogContent className='w-[calc(100vw-2rem)] overflow-visible sm:max-w-3xl'>
      <DialogHeader>
        <DialogTitle>Add New Task</DialogTitle>
        <DialogDescription className='sr-only'>
          Select a task type, assignee, and date for this episode.
        </DialogDescription>
      </DialogHeader>
      <div className='space-y-4'>
        <div>
          <label className='text-sm font-medium'>Task Type</label>
          <div className='mt-1'>
            <TaskTypeCombobox
              options={addTaskOptions}
              value={selectedAddTaskOptionId}
              onValueChange={option => {
                setSelectedAddTaskOptionId(option.id);
                setNewCommNotes('');
              }}
              filter={addTaskTypeFilter}
              onFilterChange={setAddTaskTypeFilter}
              modal={true}
              disabled={isCreatingTask}
            />
          </div>
        </div>

        {!selectedAddTaskIsThirtyDaySummary && (
          <div>
            <ClinicianSelect
              label='Assignee'
              name='add-task-assignee'
              inputId='add-task-assignee'
              placeholder='Select assignee'
              clinicians={addTaskAssignableClinicians}
              value={newTaskAssignee}
              onChange={event => setNewTaskAssignee(event.target.value)}
              disabled={isCreatingTask}
              isClearable={false}
              optionLabelFormatter={formatDuplicateClinicianOptionLabel}
              formatOptionLabel={(option, { context }) =>
                context === 'menu' ? (
                  <span className='flex w-full items-center justify-between gap-3'>
                    <span className='truncate'>{option.label}</span>
                    <ClinicianLoadBadge
                      week={addTaskLoadWeek}
                      loadsByUserId={addTaskLoadsByUserId}
                      userId={option.user_id}
                    />
                  </span>
                ) : (
                  option.label
                )
              }
            />
            {/* Why a load is or is not shown, said rather than left blank —
                but only to someone who has the permission to see loads at
                all. Telling anyone else about a number they can never see
                would be describing a feature they do not have. */}
            {canViewSchedulingPointLoads &&
              (!addTaskTargetClinicId ? (
                <p className='mt-1.5 text-xs text-muted-foreground'>
                  {patientClinicId
                    ? "Loads aren't shown — this episode has no clinic."
                    : "This patient has no clinic — loads aren't shown."}
                </p>
              ) : !addTaskDateYmd ? (
                <p className='mt-1.5 text-xs text-muted-foreground'>
                  Pick a date to see loads.
                </p>
              ) : (
                <ClinicianLoadDetail
                  week={addTaskLoadWeek}
                  load={selectedAddTaskLoad}
                />
              ))}
          </div>
        )}

        {(selectedAddTaskIsOrder || selectedAddTaskIsCommunication) && (
          <div>
            <DropdownField
              label='Physician'
              htmlFor='add-task-physician'
              sortOptions
              placeholder='Select physician'
              searchPlaceholder='Search physicians...'
              enableFiltering
              isClearable={false}
              options={addTaskPhysicianOptions.map(physician => ({
                value: physician.id,
                label: formatPhysicianName(physician),
              }))}
              value={newTaskPhysicianId}
              onChange={setNewTaskPhysicianId}
              disabled={isCreatingTask}
            />
          </div>
        )}

        <div>
          <label className='text-sm font-medium'>
            {selectedAddTaskIsCommunication || selectedAddTaskIsMisc
              ? 'Date'
              : 'Target Date'}
          </label>
          <div className='mt-1'>
            <DatePicker
              date={newTaskDate}
              onDateChange={setBoundedNewTaskDate}
              minDate={
                addTaskTargetEpisode?.start_date
                  ? parseISOToLocalDate(addTaskTargetEpisode.start_date)
                  : undefined
              }
              maxDate={
                addTaskTargetEpisode?.end_date
                  ? parseISOToLocalDate(addTaskTargetEpisode.end_date)
                  : undefined
              }
            />
          </div>
        </div>

        {addTaskBlockedByInactiveEpisode && (
          <p
            id='add-task-inactive-episode'
            data-testid='add-task-inactive-episode'
            role='alert'
            className='flex items-start gap-2 text-sm text-destructive'
          >
            <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' />
            <span>
              {patientHasActiveEpisode
                ? 'This episode is not active, so visits cannot be scheduled on it. Switch to the active episode to add this task.'
                : 'This patient has no active episode, so no new visits can be scheduled.'}
            </span>
          </p>
        )}

        {selectedAddTaskIsCommunication &&
          !selectedAddTaskIsThirtyDaySummary && (
            <div>
              <label className='text-sm font-medium'>
                Communication Details
              </label>
              <textarea
                value={newCommNotes}
                onChange={e => setNewCommNotes(e.target.value)}
                placeholder='Describe the communication and any follow-up actions…'
                className='mt-1 w-full min-h-[100px] resize-y rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                disabled={isCreatingTask}
              />
            </div>
          )}

        {addTaskCapacityBlock && (
          <p
            role='alert'
            className='rounded border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive'
          >
            {addTaskCapacityBlock.message}
          </p>
        )}
      </div>
      <DialogFooter>
        <Button
          variant='outline'
          onClick={closeAddTaskDialog}
          disabled={isCreatingTask}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreateTask}
          aria-describedby={
            addTaskBlockedByInactiveEpisode
              ? 'add-task-inactive-episode'
              : undefined
          }
          disabled={
            isCreatingTask ||
            !selectedAddTaskOption ||
            !!addTaskCapacityBlock ||
            addTaskBlockedByInactiveEpisode ||
            (!selectedAddTaskIsCommunication && !newTaskAssignee) ||
            ((selectedAddTaskIsOrder || selectedAddTaskIsCommunication) &&
              !newTaskPhysicianId) ||
            (selectedAddTaskIsCommunication &&
              !selectedAddTaskIsThirtyDaySummary &&
              !newCommNotes.trim())
          }
        >
          {isCreatingTask ? 'Creating...' : 'Add Task'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

export default AddTaskDialog;
